import React from 'react';

type BadgeVariant = 'owner' | 'renter' | 'resident' | 'multipleUnitOwner' | 'sponsor' | 'vouched' | 'solved' | 'pinned' | 'ownersOnly' | 'verified' | 'pending' | 'question' | 'recommendation' | 'discussion' | 'offer' | 'alert' | 'poll' | 'available' | 'pendingSale' | 'sold';

interface BadgeProps {
  variant: BadgeVariant;
  label?: string;
  size?: 'sm' | 'md';
}

const variantStyles: Record<BadgeVariant, { bg: string; color: string; border: string }> = {
  owner: { bg: 'rgba(76,201,255,0.10)', color: '#4CC9FF', border: 'rgba(76,201,255,0.20)' },
  renter: { bg: 'rgba(167,163,196,0.10)', color: '#A7A3C4', border: 'rgba(167,163,196,0.20)' },
  resident: { bg: 'rgba(122,92,255,0.10)', color: '#9B7FFF', border: 'rgba(122,92,255,0.20)' },
  multipleUnitOwner: { bg: 'rgba(76,201,255,0.08)', color: '#4CC9FF', border: 'rgba(76,201,255,0.18)' },
  sponsor: { bg: 'rgba(122,92,255,0.10)', color: '#7A5CFF', border: 'rgba(122,92,255,0.20)' },
  vouched: { bg: 'rgba(26,138,90,0.12)', color: '#2DD4A0', border: 'rgba(26,138,90,0.25)' },
  solved: { bg: 'rgba(26,138,90,0.12)', color: '#2DD4A0', border: 'rgba(26,138,90,0.25)' },
  pinned: { bg: 'rgba(185,123,26,0.12)', color: '#F0A832', border: 'rgba(185,123,26,0.25)' },
  ownersOnly: { bg: 'rgba(76,201,255,0.08)', color: '#4CC9FF', border: 'rgba(76,201,255,0.18)' },
  verified: { bg: 'rgba(26,138,90,0.12)', color: '#2DD4A0', border: 'rgba(26,138,90,0.25)' },
  pending: { bg: 'rgba(185,123,26,0.12)', color: '#F0A832', border: 'rgba(185,123,26,0.25)' },
  question: { bg: 'rgba(76,201,255,0.08)', color: '#4CC9FF', border: 'rgba(76,201,255,0.18)' },
  recommendation: { bg: 'rgba(122,92,255,0.10)', color: '#9B7FFF', border: 'rgba(122,92,255,0.20)' },
  discussion: { bg: 'rgba(167,163,196,0.08)', color: '#A7A3C4', border: 'rgba(167,163,196,0.18)' },
  offer: { bg: 'rgba(26,138,90,0.10)', color: '#2DD4A0', border: 'rgba(26,138,90,0.20)' },
  alert: { bg: 'rgba(192,57,43,0.12)', color: '#FF6B6B', border: 'rgba(192,57,43,0.25)' },
  poll: { bg: 'rgba(255,61,245,0.08)', color: '#FF3DF5', border: 'rgba(255,61,245,0.18)' },
  available: { bg: 'rgba(26,138,90,0.12)', color: '#2DD4A0', border: 'rgba(26,138,90,0.25)' },
  pendingSale: { bg: 'rgba(185,123,26,0.12)', color: '#F0A832', border: 'rgba(185,123,26,0.25)' },
  sold: { bg: 'rgba(167,163,196,0.08)', color: '#A7A3C4', border: 'rgba(167,163,196,0.18)' },
};

const defaultLabels: Record<BadgeVariant, string> = {
  owner: 'Owner',
  renter: 'Renter',
  resident: 'Resident',
  multipleUnitOwner: 'Multiple Unit Owner',
  sponsor: 'Sponsor',
  vouched: 'Vouched',
  solved: 'Solved',
  pinned: 'Pinned',
  ownersOnly: 'Owners Only',
  verified: 'Verified',
  pending: 'Pending',
  question: 'Question',
  recommendation: 'Recommendation',
  discussion: 'Discussion',
  offer: 'Offer',
  alert: 'Alert',
  poll: 'Poll',
  available: 'Available',
  pendingSale: 'Pending',
  sold: 'Sold',
};

export default function Badge({ variant, label, size = 'sm' }: BadgeProps) {
  const style = variantStyles[variant];
  const text = label ?? defaultLabels[variant];
  const padding = size === 'sm' ? '2px 8px' : '4px 10px';
  const fontSize = size === 'sm' ? '11px' : '12px';

  return (
    <span
      className="inline-flex items-center font-medium rounded-md"
      style={{
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        padding,
        fontSize,
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      {text}
    </span>
  );
}
